import { Lock, LockOpen, Minus, Plus, Trash2, Type, Users } from 'lucide-react'
import { useState } from 'react'
import Card from 'app/frontend/base/components/Card'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import {
  ColorPicker,
  ColorPickerAlphaSlider,
  ColorPickerArea,
  ColorPickerContent,
  ColorPickerHueSlider,
  ColorPickerInput,
  ColorPickerSwatch,
  ColorPickerTrigger,
} from '@/components/ui/color-picker'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import {
  DEFAULT_FILL_COLOR,
  DEFAULT_ICON_OPACITY,
  DEFAULT_ICON_STROKE_COLOR,
  DEFAULT_ICON_STROKE_WIDTH,
  DEFAULT_LABEL_FONT_SIZE,
  DEFAULT_STROKE_COLOR,
  DEFAULT_TEXT_BACKGROUND,
  DEFAULT_TEXT_COLOR,
  DEFAULT_TEXT_FONT_SIZE,
  LABEL_FONT_SIZE_STEP,
  MAX_ENTITY_COUNT,
  MAX_TEXT_FONT_SIZE,
  MIN_TEXT_FONT_SIZE,
  TEXT_FONT_SIZE_STEP,
} from '../constants'
import { classNameForSpecId, specsForClassName } from '../iconKinds'
import { isClosedShape } from '../shapes/entityPositions'
import {
  IconShape,
  isEncounterCreatureShape,
  isFramedIconShape,
  isIconShape,
} from '../shapes/iconShapes'
import { shapeDisplayLabel } from '../shapes/shapeLabels'
import { cn } from '@/lib/utils'

const commonValue = <T,>(values: T[]): T | undefined =>
  values.length > 0 && values.every((v) => v === values[0])
    ? values[0]
    : undefined

type ShapeNameInputProps = {
  shape: VisualShape
  onUpdate: (updater: (shape: VisualShape) => VisualShape) => void
}

const ShapeNameInput = ({ shape, onUpdate }: ShapeNameInputProps) => (
  <input
    value={shape.name ?? ''}
    onChange={(e) =>
      onUpdate((current) => ({
        ...current,
        name: e.target.value || undefined,
      }))
    }
    placeholder={shapeDisplayLabel(shape)}
    className='field-sizing-content min-w-8 bg-transparent text-xs font-medium text-muted-foreground outline-none'
  />
)

type ShapeLabelToggleProps = {
  shape: VisualShape
  onUpdate: (updater: (shape: VisualShape) => VisualShape) => void
}

const clampLabelFontSize = (value: number) =>
  Math.min(MAX_TEXT_FONT_SIZE, Math.max(MIN_TEXT_FONT_SIZE, value))

const ShapeLabelToggle = ({ shape, onUpdate }: ShapeLabelToggleProps) => {
  const enabled = shape.show_label ?? false
  const fontSize = shape.label_font_size ?? DEFAULT_LABEL_FONT_SIZE

  return (
    <div className='flex items-center gap-1'>
      <Switch
        checked={enabled}
        onCheckedChange={(checked) =>
          onUpdate((current) => ({
            ...current,
            show_label: checked,
            label_font_size: current.label_font_size ?? DEFAULT_LABEL_FONT_SIZE,
          }))
        }
      />
      <Type className='h-3.5 w-3.5 text-muted-foreground' />
      {enabled ? (
        <>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='h-5 w-5'
            disabled={fontSize <= MIN_TEXT_FONT_SIZE}
            onClick={() =>
              onUpdate((current) => ({
                ...current,
                label_font_size: clampLabelFontSize(
                  (current.label_font_size ?? DEFAULT_LABEL_FONT_SIZE) -
                    LABEL_FONT_SIZE_STEP,
                ),
              }))
            }
          >
            <Minus className='h-3 w-3' />
          </Button>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='h-5 w-5'
            disabled={fontSize >= MAX_TEXT_FONT_SIZE}
            onClick={() =>
              onUpdate((current) => ({
                ...current,
                label_font_size: clampLabelFontSize(
                  (current.label_font_size ?? DEFAULT_LABEL_FONT_SIZE) +
                    LABEL_FONT_SIZE_STEP,
                ),
              }))
            }
          >
            <Plus className='h-3 w-3' />
          </Button>
        </>
      ) : null}
    </div>
  )
}

type EntityCountInputProps = {
  shape: VisualShape
  onUpdate: (updater: (shape: VisualShape) => VisualShape) => void
}

const clampEntityCount = (value: number) =>
  Math.min(MAX_ENTITY_COUNT, Math.max(0, Math.floor(value)))

const EntityCountInput = ({ shape, onUpdate }: EntityCountInputProps) => {
  const [raw, setRaw] = useState<string>(String(shape.entity_count ?? 0))

  return (
    <div className='flex items-center gap-1'>
      <span className='text-xs text-muted-foreground'>Assign</span>
      <Input
        type='number'
        min={0}
        max={MAX_ENTITY_COUNT}
        value={raw}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          setRaw(e.target.value)
          const parsed = parseInt(e.target.value, 10)
          if (!Number.isNaN(parsed)) {
            const clamped = clampEntityCount(parsed)
            onUpdate((current) => ({ ...current, entity_count: clamped }))
          }
        }}
        onBlur={() => {
          const parsed = parseInt(raw, 10)
          const clamped = Number.isNaN(parsed) ? 0 : clampEntityCount(parsed)
          setRaw(String(clamped))
          onUpdate((current) => ({ ...current, entity_count: clamped }))
        }}
        className='h-7 w-8 px-1 text-xs text-center'
      />
      <Users className='h-3.5 w-3.5 text-foreground' />
    </div>
  )
}

type AssignToggleProps = {
  shape: VisualShape
  onUpdate: (updater: (shape: VisualShape) => VisualShape) => void
}

const AssignToggle = ({ shape, onUpdate }: AssignToggleProps) => {
  const assigned = (shape.entity_count ?? 0) > 0

  return (
    <label className='flex items-center gap-2 text-xs text-muted-foreground'>
      Assign
      <Switch
        checked={assigned}
        onCheckedChange={(checked) =>
          onUpdate((current) => ({
            ...current,
            entity_count: checked ? 1 : 0,
          }))
        }
      />
      <Users className='h-3.5 w-3.5' />
    </label>
  )
}

type DirectionToggleProps = {
  shapes: VisualShape[]
  onUpdate: (updater: (shape: VisualShape) => VisualShape) => void
}

const DirectionToggle = ({ shapes, onUpdate }: DirectionToggleProps) => {
  const shown = shapes.every(
    (shape) => !isIconShape(shape) || (shape.show_direction ?? true),
  )

  return (
    <label className='flex items-center gap-2 text-xs text-muted-foreground'>
      Direction
      <Switch
        checked={shown}
        onCheckedChange={(checked) =>
          onUpdate((current) =>
            isEncounterCreatureShape(current)
              ? { ...current, show_direction: checked }
              : current,
          )
        }
      />
    </label>
  )
}

type SpecIconSwitcherProps = {
  shape: IconShape
  onUpdate: (updater: (shape: VisualShape) => VisualShape) => void
}

const SpecIconSwitcher = ({ shape, onUpdate }: SpecIconSwitcherProps) => {
  if (shape.kind !== 'class' && shape.kind !== 'spec') return null

  const className =
    shape.kind === 'class' ? shape.value : classNameForSpecId(shape.value)
  if (!className) return null

  const specs = specsForClassName(className)
  if (specs.length === 0) return null

  const activeSpecId = shape.kind === 'spec' ? shape.value : undefined

  return (
    <div className='flex items-center gap-2'>
      <span className='text-xs text-muted-foreground'>Spec</span>
      <div className='flex items-center gap-1'>
        {specs.map((spec) => {
          const isActive = activeSpecId === spec.id
          return (
            <Tooltip key={spec.id} delayDuration={300}>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  aria-label={spec.name}
                  aria-pressed={isActive}
                  className={cn(
                    'rounded-md p-0.5 transition-colors',
                    isActive
                      ? 'ring-2 ring-primary ring-offset-1 ring-offset-background'
                      : 'hover:bg-muted',
                  )}
                  onClick={() =>
                    onUpdate((current) => {
                      if (!isIconShape(current)) return current
                      if (isActive) {
                        return { ...current, kind: 'class', value: className }
                      }
                      return { ...current, kind: 'spec', value: spec.id }
                    })
                  }
                >
                  <BlizzardIcon
                    iconName={spec.icon}
                    className='h-5 w-5 min-h-5 min-w-5'
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent>{spec.name}</TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </div>
  )
}

type Props = {
  selectedShapes: VisualShape[]
  onUpdate: (updater: (shape: VisualShape) => VisualShape) => void
  onRemove: () => void
  onEditText?: () => void
}

const ShapePropertiesToolbar = ({
  selectedShapes,
  onUpdate,
  onRemove,
  onEditText,
}: Props) => {
  if (selectedShapes.length === 0) return null

  const singleShape =
    selectedShapes.length === 1 ? selectedShapes[0] : undefined

  const multiLabel = `${selectedShapes.length} objects selected`

  const sharedFill = commonValue(selectedShapes.map((s) => s.fill_rgb))
  const sharedStroke = commonValue(
    selectedShapes.map((s) => s.stroke_rgb ?? s.rgb),
  )
  const sharedWidth = commonValue(selectedShapes.map((s) => s.stroke_width))
  const sharedFontSize = commonValue(
    selectedShapes.map((s) => (s.type === 'text' ? s.font_size : undefined)),
  )
  const sharedOpacity = commonValue(
    selectedShapes.map((s) =>
      isIconShape(s) ? s.opacity ?? DEFAULT_ICON_OPACITY : undefined,
    ),
  )
  const sharedIconStroke = commonValue(
    selectedShapes.map((s) =>
      isFramedIconShape(s)
        ? s.stroke_rgb ?? DEFAULT_ICON_STROKE_COLOR
        : undefined,
    ),
  )
  const sharedIconStrokeWidth = commonValue(
    selectedShapes.map((s) =>
      isFramedIconShape(s)
        ? s.stroke_width ?? DEFAULT_ICON_STROKE_WIDTH
        : undefined,
    ),
  )

  const allLocked = selectedShapes.every((s) => s.locked)
  const allIcons = selectedShapes.every((s) => isIconShape(s))
  const allFramedIcons = selectedShapes.every((s) => isFramedIconShape(s))
  const allText = selectedShapes.every((s) => s.type === 'text')
  const showAssign = singleShape !== undefined && isClosedShape(singleShape)
  const showIconAssign =
    singleShape !== undefined &&
    isIconShape(singleShape) &&
    singleShape.kind !== 'ability' &&
    (singleShape.kind !== 'encounter' || isEncounterCreatureShape(singleShape))
  const showDirectionToggle = selectedShapes.every(isEncounterCreatureShape)
  const showSpecSwitcher =
    singleShape !== undefined &&
    isIconShape(singleShape) &&
    (singleShape.kind === 'class' || singleShape.kind === 'spec')

  return (
    <Card className='pointer-events-auto relative px-3 py-1 bg-card/70 hover:bg-card/90 transition-colors'>
      <div className='flex flex-wrap items-center gap-3'>
        {allIcons ? (
          <>
            <div className='flex items-center gap-2'>
              <span className='text-xs text-muted-foreground'>Opacity</span>
              <Slider
                min={0}
                max={100}
                step={1}
                value={[
                  Math.round(
                    (sharedOpacity ??
                      (selectedShapes[0] && isIconShape(selectedShapes[0])
                        ? selectedShapes[0].opacity ?? DEFAULT_ICON_OPACITY
                        : DEFAULT_ICON_OPACITY)) * 100,
                  ),
                ]}
                onValueChange={([value]) =>
                  value !== undefined &&
                  onUpdate((current) => ({
                    ...current,
                    opacity: value / 100,
                  }))
                }
                className={
                  sharedOpacity === undefined
                    ? 'w-28 **:[[role=slider]]:border-dashed **:[[role=slider]]:border-muted-foreground/50 **:[[role=slider]]:bg-background'
                    : 'w-28'
                }
              />
            </div>

            {allFramedIcons ? (
              <>
                <span className='text-xs text-muted-foreground'>Stroke</span>
                <ColorPicker
                  className='flex items-center'
                  value={sharedIconStroke}
                  defaultValue={DEFAULT_ICON_STROKE_COLOR}
                  format='rgb'
                  onValueChange={(color) =>
                    onUpdate((current) => ({ ...current, stroke_rgb: color }))
                  }
                >
                  <ColorPickerTrigger asChild>
                    <Button variant='ghost' className='h-auto w-auto p-0'>
                      {sharedIconStroke ? (
                        <ColorPickerSwatch className='h-6 w-9 rounded-md transition-colors hover:border-muted-foreground' />
                      ) : (
                        <div className='h-6 w-9 rounded-md border border-dashed border-muted-foreground/50 transition-colors hover:border-muted-foreground' />
                      )}
                    </Button>
                  </ColorPickerTrigger>
                  <ColorPickerContent>
                    <ColorPickerArea />
                    <ColorPickerHueSlider />
                    <ColorPickerAlphaSlider />
                    <ColorPickerInput withoutAlpha />
                  </ColorPickerContent>
                </ColorPicker>

                <div className='flex items-center gap-2'>
                  <span className='text-xs text-muted-foreground'>Width</span>
                  <Slider
                    min={0}
                    max={10}
                    step={1}
                    value={[
                      sharedIconStrokeWidth ??
                        (selectedShapes[0] &&
                        isFramedIconShape(selectedShapes[0])
                          ? selectedShapes[0].stroke_width ??
                            DEFAULT_ICON_STROKE_WIDTH
                          : DEFAULT_ICON_STROKE_WIDTH),
                    ]}
                    onValueChange={([value]) =>
                      value !== undefined &&
                      onUpdate((current) => ({
                        ...current,
                        stroke_width: value,
                      }))
                    }
                    className={
                      sharedIconStrokeWidth === undefined
                        ? 'w-28 **:[[role=slider]]:border-dashed **:[[role=slider]]:border-muted-foreground/50 **:[[role=slider]]:bg-background'
                        : 'w-28'
                    }
                  />
                </div>
              </>
            ) : null}
          </>
        ) : allText ? (
          <>
            <span className='text-xs text-muted-foreground'>Text</span>
            <ColorPicker
              className='flex items-center'
              value={sharedFill}
              defaultValue={DEFAULT_TEXT_COLOR}
              format='rgb'
              onValueChange={(color) =>
                onUpdate((current) => ({ ...current, fill_rgb: color }))
              }
            >
              <ColorPickerTrigger asChild>
                <Button variant='ghost' className='h-auto w-auto p-0'>
                  {sharedFill ? (
                    <ColorPickerSwatch className='h-6 w-9 rounded-md transition-colors hover:border-muted-foreground' />
                  ) : (
                    <div className='h-6 w-9 rounded-md border border-dashed border-muted-foreground/50 transition-colors hover:border-muted-foreground' />
                  )}
                </Button>
              </ColorPickerTrigger>
              <ColorPickerContent>
                <ColorPickerArea />
                <ColorPickerHueSlider />
                <ColorPickerAlphaSlider />
                <ColorPickerInput withoutAlpha />
              </ColorPickerContent>
            </ColorPicker>

            <span className='text-xs text-muted-foreground'>Background</span>
            <ColorPicker
              className='flex items-center'
              value={sharedStroke}
              defaultValue={DEFAULT_TEXT_BACKGROUND}
              format='rgb'
              onValueChange={(color) =>
                onUpdate((current) => ({ ...current, stroke_rgb: color }))
              }
            >
              <ColorPickerTrigger asChild>
                <Button variant='ghost' className='h-auto w-auto p-0'>
                  {sharedStroke ? (
                    <ColorPickerSwatch className='h-6 w-9 rounded-md transition-colors hover:border-muted-foreground' />
                  ) : (
                    <div className='h-6 w-9 rounded-md border border-dashed border-muted-foreground/50 transition-colors hover:border-muted-foreground' />
                  )}
                </Button>
              </ColorPickerTrigger>
              <ColorPickerContent>
                <ColorPickerArea />
                <ColorPickerHueSlider />
                <ColorPickerAlphaSlider />
                <ColorPickerInput withoutAlpha />
              </ColorPickerContent>
            </ColorPicker>

            <div className='flex items-center gap-2'>
              <span className='text-xs text-muted-foreground'>Size</span>
              <Slider
                min={MIN_TEXT_FONT_SIZE}
                max={MAX_TEXT_FONT_SIZE}
                step={TEXT_FONT_SIZE_STEP}
                value={[
                  sharedFontSize ??
                    (selectedShapes[0]?.type === 'text'
                      ? selectedShapes[0].font_size
                      : DEFAULT_TEXT_FONT_SIZE),
                ]}
                onValueChange={([value]) =>
                  value !== undefined &&
                  onUpdate((current) =>
                    current.type === 'text'
                      ? { ...current, font_size: value }
                      : current,
                  )
                }
                className={
                  sharedFontSize === undefined
                    ? 'w-28 **:[[role=slider]]:border-dashed **:[[role=slider]]:border-muted-foreground/50 **:[[role=slider]]:bg-background'
                    : 'w-28'
                }
              />
            </div>

            {singleShape?.type === 'text' && onEditText ? (
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='h-7 gap-1.5 px-2 text-xs'
                onClick={onEditText}
              >
                <Type className='h-3.5 w-3.5' />
                Edit text
              </Button>
            ) : null}
          </>
        ) : (
          <>
            <span className='text-xs text-muted-foreground'>Fill</span>
            <ColorPicker
              className='flex items-center'
              value={sharedFill}
              defaultValue={DEFAULT_FILL_COLOR}
              format='rgb'
              onValueChange={(color) =>
                onUpdate((current) => ({ ...current, fill_rgb: color }))
              }
            >
              <ColorPickerTrigger asChild>
                <Button variant='ghost' className='h-auto w-auto p-0'>
                  {sharedFill ? (
                    <ColorPickerSwatch className='h-6 w-9 rounded-md transition-colors hover:border-muted-foreground' />
                  ) : (
                    <div className='h-6 w-9 rounded-md border border-dashed border-muted-foreground/50 transition-colors hover:border-muted-foreground' />
                  )}
                </Button>
              </ColorPickerTrigger>
              <ColorPickerContent>
                <ColorPickerArea />
                <ColorPickerHueSlider />
                <ColorPickerAlphaSlider />
                <ColorPickerInput withoutAlpha />
              </ColorPickerContent>
            </ColorPicker>

            <span className='text-xs text-muted-foreground'>Stroke</span>
            <ColorPicker
              className='flex items-center'
              value={sharedStroke}
              defaultValue={DEFAULT_STROKE_COLOR}
              format='rgb'
              onValueChange={(color) =>
                onUpdate((current) => ({ ...current, stroke_rgb: color }))
              }
            >
              <ColorPickerTrigger asChild>
                <Button variant='ghost' className='h-auto w-auto p-0'>
                  {sharedStroke ? (
                    <ColorPickerSwatch className='h-6 w-9 rounded-md transition-colors hover:border-muted-foreground' />
                  ) : (
                    <div className='h-6 w-9 rounded-md border border-dashed border-muted-foreground/50 transition-colors hover:border-muted-foreground' />
                  )}
                </Button>
              </ColorPickerTrigger>
              <ColorPickerContent>
                <ColorPickerArea />
                <ColorPickerHueSlider />
                <ColorPickerAlphaSlider />
                <ColorPickerInput withoutAlpha />
              </ColorPickerContent>
            </ColorPicker>

            <div className='flex items-center gap-2'>
              <span className='text-xs text-muted-foreground'>Width</span>
              <Slider
                min={0}
                max={10}
                step={1}
                value={[sharedWidth ?? selectedShapes[0]?.stroke_width ?? 3]}
                onValueChange={([value]) =>
                  value !== undefined &&
                  onUpdate((current) => ({ ...current, stroke_width: value }))
                }
                className={
                  sharedWidth === undefined
                    ? 'w-28 **:[[role=slider]]:border-dashed **:[[role=slider]]:border-muted-foreground/50 **:[[role=slider]]:bg-background'
                    : 'w-28'
                }
              />
            </div>
          </>
        )}

        {showAssign && singleShape ? (
          <EntityCountInput
            key={singleShape.id}
            shape={singleShape}
            onUpdate={onUpdate}
          />
        ) : null}

        {showIconAssign && singleShape ? (
          <AssignToggle
            key={singleShape.id}
            shape={singleShape}
            onUpdate={onUpdate}
          />
        ) : null}

        {showDirectionToggle ? (
          <DirectionToggle shapes={selectedShapes} onUpdate={onUpdate} />
        ) : null}

        {showSpecSwitcher && singleShape && isIconShape(singleShape) ? (
          <SpecIconSwitcher shape={singleShape} onUpdate={onUpdate} />
        ) : null}

        <div className='ml-auto flex items-center gap-1'>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-7 w-7'
                onClick={() =>
                  onUpdate((current) => ({ ...current, locked: !allLocked }))
                }
              >
                {allLocked ? (
                  <LockOpen className='h-4 w-4' />
                ) : (
                  <Lock className='h-4 w-4' />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {allLocked ? 'Unlock object' : 'Lock object'}
              {selectedShapes.length > 1 ? 's' : ''}
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-7 w-7 text-destructive-foreground'
                onClick={onRemove}
              >
                <Trash2 className='h-4 w-4' />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Delete object{selectedShapes.length > 1 ? 's' : ''}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {singleShape ? (
        <div className='absolute left-3 top-full mt-px flex items-center gap-1.5 rounded-b-md border border-t-0 border-inherit bg-inherit px-2 py-0.5'>
          <ShapeNameInput
            key={singleShape.id}
            shape={singleShape}
            onUpdate={onUpdate}
          />
          {singleShape.type !== 'text' ? (
            <ShapeLabelToggle
              key={`${singleShape.id}-label`}
              shape={singleShape}
              onUpdate={onUpdate}
            />
          ) : null}
        </div>
      ) : (
        <span className='absolute left-3 top-full mt-px rounded-b-md border border-t-0 border-inherit bg-inherit px-2 py-0.5 text-xs font-medium text-muted-foreground'>
          {multiLabel}
        </span>
      )}
    </Card>
  )
}

export default ShapePropertiesToolbar
